import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useEffect, useState, useCallback } from "react";
import { useTheme } from "@/hooks/useTheme";

// Generate array of frame paths - prefer WebP, fallback to JPG
const TOTAL_FRAMES = 25;

// Check WebP support
const supportsWebP = (() => {
  if (typeof document === 'undefined') return false;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
})();

const HERO_FRAMES = Array.from({ length: TOTAL_FRAMES }, (_, i) => {
  const num = String(i + 1).padStart(2, '0');
  return supportsWebP 
    ? `/images/hero-frames-optimized/frame_${num}.webp`
    : `/images/hero-frames/frame_${num}.jpg`;
});

export const Hero = () => {
  const buttonRef = useRef(null);
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  
  // Smooth animation state - all refs for performance
  const targetFrameRef = useRef(0);
  const currentFrameRef = useRef(0);
  const displayedFrameRef = useRef(0);
  const animationFrameRef = useRef(null);
  const isAnimatingRef = useRef(false);
  
  // Track loaded images count
  const loadedCountRef = useRef(0);

  // Preload all images into memory
  useEffect(() => {
    const images = [];
    let loaded = 0;
    
    HERO_FRAMES.forEach((src, index) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        loaded++;
        if (loaded === TOTAL_FRAMES) {
          imagesRef.current = images;
          setImagesLoaded(true);
          // Draw first frame
          drawFrame(0);
        }
      };
      img.src = src;
      images[index] = img;
    });
  }, []);

  // Draw frame to canvas - much faster than DOM manipulation
  const drawFrame = useCallback((frameIndex) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { 
      alpha: false,
      desynchronized: true  // Better performance on some browsers
    });
    const img = imagesRef.current[frameIndex];
    
    if (canvas && ctx && img) {
      // Set canvas size to match container if needed
      const containerWidth = canvas.offsetWidth || 1920;
      const containerHeight = canvas.offsetHeight || 1080;
      
      if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
        canvas.width = containerWidth;
        canvas.height = containerHeight;
      }
      
      // Set image smoothing for better quality (Firefox friendly)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'medium';
      
      // Draw image covering entire canvas
      const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
      const x = (canvas.width - img.width * scale) / 2;
      const y = (canvas.height - img.height * scale) / 2;
      
      ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
      displayedFrameRef.current = frameIndex;
    }
  }, []);

  // Handle window resize - redraw current frame
  useEffect(() => {
    const handleResize = () => {
      if (imagesLoaded) {
        drawFrame(displayedFrameRef.current);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawFrame, imagesLoaded]);

  // Optimized animation loop - only runs when needed
  const animateFrame = useCallback(() => {
    const target = targetFrameRef.current;
    const current = currentFrameRef.current;
    
    // Check if we're close enough to stop
    const diff = Math.abs(target - current);
    if (diff < 0.01) {
      currentFrameRef.current = target;
      const finalFrame = Math.round(target);
      if (finalFrame !== displayedFrameRef.current) {
        drawFrame(finalFrame);
      }
      isAnimatingRef.current = false;
      return;
    }
    
    // Faster lerp for more responsive feel
    const lerpFactor = 0.12;
    const newFrame = current + (target - current) * lerpFactor;
    currentFrameRef.current = newFrame;
    
    const frameIndex = Math.round(newFrame);
    if (frameIndex !== displayedFrameRef.current && frameIndex >= 0 && frameIndex < TOTAL_FRAMES) {
      drawFrame(frameIndex);
    }
    
    animationFrameRef.current = requestAnimationFrame(animateFrame);
  }, [drawFrame]);

  // Start animation only when target changes
  const startAnimation = useCallback(() => {
    if (!isAnimatingRef.current) {
      isAnimatingRef.current = true;
      animationFrameRef.current = requestAnimationFrame(animateFrame);
    }
  }, [animateFrame]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Calculate distance from mouse to button center and set target frame
  const handleMouseMove = useCallback((e) => {
    if (!buttonRef.current || !imagesLoaded) return;

    const button = buttonRef.current;
    const buttonRect = button.getBoundingClientRect();
    const section = sectionRef.current;
    const sectionRect = section?.getBoundingClientRect();

    // Button center coordinates
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const buttonCenterY = buttonRect.top + buttonRect.height / 2;

    // Mouse coordinates
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    // Calculate distance from mouse to button center
    const distance = Math.sqrt(
      Math.pow(mouseX - buttonCenterX, 2) + 
      Math.pow(mouseY - buttonCenterY, 2)
    );

    // Store previous target to detect changes
    const prevTarget = targetFrameRef.current;
    
    // Animation radius - fixed 350px from button
    const animationRadius = 350;
    
    if (distance > animationRadius) {
      targetFrameRef.current = 0;
    } else {
      const animNormalizedDist = distance / animationRadius;
      targetFrameRef.current = (1 - animNormalizedDist) * (TOTAL_FRAMES - 1);
    }
    
    // Only start animation if target changed significantly
    if (Math.abs(targetFrameRef.current - prevTarget) > 0.01) {
      startAnimation();
    }
  }, [imagesLoaded, startAnimation]);

  // Handle touch for mobile
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    }
  }, [handleMouseMove]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    section.addEventListener('mousemove', handleMouseMove, { passive: true });
    section.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      section.removeEventListener('mousemove', handleMouseMove);
      section.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleMouseMove, handleTouchMove]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      aria-label="Serwis aparatury medycznej"
      data-testid="hero-section"
    >
      {/* Canvas-based animation - much faster than DOM image swapping */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: isDark
              ? 'brightness(0.75) saturate(1.1) contrast(1.05)'
              : 'brightness(1.05) saturate(1.0) contrast(1.0)',
          }}
        />
        {/* Overlay for text readability */}
        <div className={`absolute inset-0 z-10 ${
          isDark
            ? "bg-gradient-to-r from-[#0D0F12]/95 via-[#0D0F12]/60 to-[#0D0F12]/30"
            : "bg-gradient-to-r from-[#F7F9F9]/75 via-[#F7F9F9]/50 to-[#F7F9F9]/20"
        }`} />
      </div>
      
      {/* Fallback background */}
      <div className={`absolute inset-0 -z-10 ${isDark ? "bg-[#0D0F12]" : "bg-[#F7F9F9]"}`} />
      
      {/* Noise texture overlay */}
      <div className="noise-overlay absolute inset-0 z-[1]" aria-hidden="true" />
      
      {/* Interactive hint - subtle glow */}
      <div 
        className="absolute inset-0 pointer-events-none z-[2]"
        aria-hidden="true"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: imagesLoaded ? (isDark ? 0.12 : 0.08) : 0 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-[#F5861A] rounded-full blur-[100px]"
        />
      </div>
      
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative z-10">
        <div className="max-w-3xl">
          {/* Text Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight ${
                isDark ? "text-white" : "text-[#212738]"
              }`}>
                Serwis
                <span className="block text-[#F5861A]">aparatury medycznej</span>
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <Link to="/zgloszenie" data-testid="hero-primary-cta-button" ref={buttonRef}>
                <Button
                  size="lg"
                  className="bg-[#F5861A] text-black font-semibold hover:bg-[#ff9734] btn-primary text-base px-8 py-6 w-full sm:w-auto relative overflow-hidden group"
                >
                  <span className="relative z-10 flex items-center">
                    Zgłoszenie serwisowe
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Button>
              </Link>
              <a href="tel:+48601486339" data-testid="hero-secondary-cta-button">
                <Button
                  size="lg"
                  variant="outline"
                  className={`text-base px-8 py-6 w-full sm:w-auto ${
                    isDark
                      ? "border-white/20 text-white hover:bg-white/10"
                      : "border-[#212738]/25 text-[#212738] hover:bg-[#212738]/5"
                  }`}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Zadzwoń teraz
                </Button>
              </a>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className={`mt-8 flex flex-wrap gap-8 text-sm ${
                isDark ? "text-white/60" : "text-[#212738]/80"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#212738]"}`}>20+</span>
                <span>lat doświadczenia</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className={`h-7 w-7 ${isDark ? "text-white" : "text-[#212738]"}`} strokeWidth={2.5} />
                <span>Wykwalifikowany zespół serwisantów</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
